import { icon } from '../utils/icons.js';

// Full-screen image viewer. Images open fitted to the window; clicking the
// image shows it at full size, scrollable when it's bigger than the screen.
export function openLightbox(images, startIndex = 0) {
  if (!images.length) return;
  let index = startIndex;
  const multiple = images.length > 1;

  const dialog = el('dialog', 'lightbox');
  dialog.setAttribute('aria-label', 'Image viewer');

  const toolbar = el('div', 'lightbox-toolbar');
  const count = el('p', 'lightbox-count');
  count.setAttribute('aria-live', 'polite');
  const actions = el('div', 'lightbox-actions');
  const original = el('a', 'lightbox-btn');
  original.target = '_blank';
  original.rel = 'noopener noreferrer';
  original.append('Open original ', svg('arrow-up-right', 14));
  const close = el('button', 'lightbox-btn lightbox-close');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.append(svg('x', 18));
  actions.append(original, close);
  toolbar.append(count, actions);

  const stage = el('div', 'lightbox-stage');
  const img = el('img', 'lightbox-img');
  img.decoding = 'async';
  stage.append(img);

  const prev = navButton('prev', 'Previous image', 'chevron-left');
  const next = navButton('next', 'Next image', 'chevron-right');
  prev.hidden = !multiple;
  next.hidden = !multiple;

  dialog.append(toolbar, stage, prev, next);

  function show(i) {
    index = (i + images.length) % images.length;
    const image = images[index];
    stage.classList.remove('is-zoomed');
    stage.scrollTo(0, 0);
    img.src = image.src;
    img.alt = image.alt || '';
    original.href = image.src;
    count.textContent = multiple ? `${index + 1} of ${images.length}` : '';
  }

  prev.addEventListener('click', () => show(index - 1));
  next.addEventListener('click', () => show(index + 1));
  close.addEventListener('click', () => dialog.close());
  img.addEventListener('click', () => stage.classList.toggle('is-zoomed'));

  // The dark area around the image closes the viewer
  stage.addEventListener('click', (e) => {
    if (e.target === stage) dialog.close();
  });

  dialog.addEventListener('keydown', (e) => {
    if (!multiple) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
  });

  let touchX = null;
  stage.addEventListener('touchstart', (e) => {
    touchX = e.touches.length === 1 ? e.touches[0].clientX : null;
  }, { passive: true });
  stage.addEventListener('touchend', (e) => {
    if (touchX === null || !multiple || stage.classList.contains('is-zoomed')) return;
    const dx = e.changedTouches[0].clientX - touchX;
    touchX = null;
    if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1));
  });

  // The dialog lives on <body>, so the router swapping the page won't remove it
  const onPopState = () => dialog.close();
  window.addEventListener('popstate', onPopState);

  const root = document.documentElement;
  const previousOverflow = root.style.overflow;
  dialog.addEventListener('close', () => {
    window.removeEventListener('popstate', onPopState);
    root.style.overflow = previousOverflow;
    dialog.remove();
  });

  show(index);
  document.body.append(dialog);
  root.style.overflow = 'hidden';
  dialog.showModal();
  close.focus();
}

function el(tag, className) {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

function svg(name, size) {
  return document.createRange().createContextualFragment(icon(name, { size }));
}

function navButton(dir, label, iconName) {
  const button = el('button', `lightbox-nav lightbox-nav--${dir}`);
  button.type = 'button';
  button.setAttribute('aria-label', label);
  button.append(svg(iconName, 22));
  return button;
}
